import gql from 'graphql-tag'

// Contrato: geofence-microservice (branch mkt-intel-beta). Nomes/campos em inglês.
// sort/sortDirection são ARRAYS pareados; metrics é campo de GeoFence com input de período.

export const GEO_FENCE_CATEGORY_FIELDS = gql`
  fragment GeoFenceCategoryFields on GeoFenceCategory {
    id
    name
    color
    isDefault
    isStore
    # geofence-microservice PR #21 (deployado no beta): linkedCount = nº de cercas vinculadas;
    # canDelete = false p/ categoria padrão OU com cercas vinculadas. Usados p/ desabilitar a
    # lixeira ANTES do clique (CategoriaSelect) + tooltip explicativo.
    linkedCount
    canDelete
  }
`

export const GEO_FENCE_FIELDS = gql`
  fragment GeoFenceFields on GeoFence {
    id
    name
    description
    # Endereço textual persistido pelo backend (geofence-microservice PR #25 / MK-20304). Quem
    # resolve é a ESCRITA: o formulário manda o endereço no upsert, e o sync preenche com o endereço
    # cadastrado da loja. Aqui só se lê. Pode vir null em cerca antiga (sem backfill) e em rascunho
    # salvo pelo formulário, porque o UpsertGeoFenceDraftInput ainda não aceita o campo — nesses
    # casos o front cai no fallback antigo (endereço da loja vinculada / reverse-geocode / coordenada).
    address
    status
    radiusMeters
    location {
      lat
      lng
    }
    category {
      ...GeoFenceCategoryFields
    }
    openingHours {
      day
      active
      startTime
      endTime
    }
    associatedUnits {
      id_erp
    }
    createdAt
    updatedAt
    inactivatedAt
    expiresAt
  }
  ${GEO_FENCE_CATEGORY_FIELDS}
`

/**
 * COBERTURA: estado atual da propagação. Não tem recorte temporal NENHUM, e isso é do backend, não
 * convenção nossa: em `get-geo-fence-metrics.use-case.ts` o `days` derivado do período vai só pro
 * `getActivityMetrics`, e os três contadores de cobertura (`installedApps`, `canReceive`,
 * `hasFence`) são consultados sem nenhum predicado de data. `coveragePercent` é `hasFence/canReceive`.
 *
 * Pedida SEM `input` de propósito (MK-21176). O serviço trata "sem período" e "30 dias" como o mesmo
 * ramo canônico, que é o único que lê e grava o cache de 2h; 60/90 dias recalculam ao vivo e não
 * persistem. Como o objeto cacheado guarda cobertura e atividade num bloco só, pedir a cobertura
 * junto com o período fazia a FONTE mudar a cada troca de filtro: 30 dias devolvia um retrato de até
 * 2h atrás e 60/90 devolviam o de agora. Medido em 2026-08-26 na mesma cerca e sessão:
 * 30d `fromCache: true`, `lastUpdatedAt` 17:57, `installedApps` 7473; 60d e 90d `fromCache: false`,
 * `lastUpdatedAt` de agora, `installedApps` 7474. `canReceive`, `hasFence` e o percentual não se
 * moveram: quem deriva é o contador global de apps da base.
 */
export const GEO_FENCE_COVERAGE_FIELDS = gql`
  fragment GeoFenceCoverageFields on GeoFenceMetrics {
    installedApps
    canReceive
    hasFence
    coveragePercent
    fromCache
    lastUpdatedAt
  }
`

// ATIVIDADE: eventos no recorte de 30/60/90 dias. É a única metade que o período afeta.
export const GEO_FENCE_ACTIVITY_FIELDS = gql`
  fragment GeoFenceActivityFields on GeoFenceMetrics {
    entries
    entriesOpen
    entriesClosed
    impactedConsumers
    medianDwellSeconds
  }
`

// Listagem: input wrapper único. variables:
// { input: { status: [GeoFenceStatus], categoryId: ID, search: String,
//            sort: [GeoFenceSortField!], sortDirection: [SortDirection!] } }
// geoFences responde ListGeoFencesResponse (geofence-microservice#16, deployado no beta em
// 21/07): sem page/pageSize no input, o serviço devolve TODAS as cercas (o mapa exige todas),
// com records = total e page = 1 — mantém a paginação client-side que já tínhamos.
export const QUERY_GEO_FENCES = gql`
  query GeoFences($input: ListGeoFencesInput) {
    geoFences(input: $input) {
      geoFences {
        ...GeoFenceFields
        # Só coveragePercent, e sem argumento de período: na listagem o resolver nunca recalcula
        # (batchContext), devolve o cachedMetrics do Mongo direto. Leitura barata pro alerta de
        # propagação baixa <1% na LINHA (UC-15).
        # ATENÇÃO ao editar comentário aqui dentro: isto é um template literal, então BACKTICK
        # fecha a string e quebra o arquivo inteiro. Custou um "Missing semicolon" e uma suíte
        # inteira sem rodar.
        metrics {
          coveragePercent
        }
      }
      records
      page
    }
  }
  ${GEO_FENCE_FIELDS}
`

/**
 * Vínculos loja↔cerca, para o alerta de loja já vinculada (MK-21580).
 *
 * Consulta PRÓPRIA, e não `store.fences`, apesar de o card sugerir reaproveitar a lista: a listagem
 * carrega com os filtros de servidor (status e busca), então quem abriu o formulário depois de filtrar
 * por "Ativa" não veria o vínculo com uma cerca Inativa ou Expirada. Alerta que depende do filtro que
 * o usuário deixou na tela anterior engana por omissão, e omissão aqui é pior que ruído: o aviso existe
 * justamente para o caso que o usuário não sabe. Mesmo motivo do `focusFences` na store.
 *
 * São três campos porque é só disso que o alerta precisa: id (para excluir a própria cerca), nome (para
 * quando o alerta evoluir para listar a cerca) e as lojas.
 */
export const QUERY_GEO_FENCE_UNIT_LINKS = gql`
  query GeoFenceUnitLinks {
    geoFences {
      geoFences {
        id
        name
        associatedUnits {
          id_erp
        }
      }
    }
  }
`

/**
 * Detalhe + métricas, com as duas metades pedidas SEPARADAS (MK-21176).
 *
 * Dois campos `metrics` no mesmo nó, um sem `input` e outro com, são VÁLIDOS porque têm ALIASES
 * diferentes: a regra de merge do GraphQL (`OverlappingFieldsCanBeMergedRule`) agrupa por *response
 * name*, e o response name é o alias quando existe, então os dois caem em grupos distintos e a
 * exigência de argumentos idênticos nem é avaliada. O conflito "Fields metrics conflict" só acontece
 * com o MESMO response name, e a própria mensagem do graphql-js prescreve o remédio: "Use different
 * aliases on the fields to fetch both if this was intentional".
 *
 * Isto contradiz um comentário que vivia aqui e afirmava o oposto. Verificado antes de mudar:
 * graphql-js 16.14.2 contra o SDL da beta valida a forma com alias, e a query foi aceita pelo
 * `apollo-platform-gateway-mkt-intel-beta` (chegou no resolver e falhou só em autenticação, não em
 * validação de campo).
 */
export const QUERY_GEO_FENCE = gql`
  query GeoFence($id: ID!, $metricsInput: GeoFenceMetricsInput) {
    geoFence(id: $id) {
      ...GeoFenceFields
      cobertura: metrics {
        ...GeoFenceCoverageFields
      }
      atividade: metrics(input: $metricsInput) {
        ...GeoFenceActivityFields
      }
    }
  }
  ${GEO_FENCE_FIELDS}
  ${GEO_FENCE_COVERAGE_FIELDS}
  ${GEO_FENCE_ACTIVITY_FIELDS}
`

/**
 * Troca de período: pede SÓ atividade.
 *
 * É o que conserta o card. Antes esta query pedia o bloco inteiro e o store substituía
 * `fence.metrics`, então cada troca de filtro reescrevia os campos de cobertura com o payload do
 * novo período — e como 30 dias vem do cache e 60/90 vêm ao vivo, o número mudava.
 */
export const QUERY_GEO_FENCE_ACTIVITY = gql`
  query GeoFenceActivity($id: ID!, $metricsInput: GeoFenceMetricsInput) {
    geoFence(id: $id) {
      id
      atividade: metrics(input: $metricsInput) {
        ...GeoFenceActivityFields
      }
    }
  }
  ${GEO_FENCE_ACTIVITY_FIELDS}
`

export const QUERY_GEO_FENCE_CATEGORIES = gql`
  query GeoFenceCategories {
    geoFenceCategories {
      ...GeoFenceCategoryFields
    }
  }
  ${GEO_FENCE_CATEGORY_FIELDS}
`

// Foco inicial do mapa (top-N por proximidade/entradas). reference obrigatória.
// variables: { reference: { limit: 10, lat, lng, selectedUnitId } } (lat/lng/selectedUnitId opcionais)
export const QUERY_ELIGIBLE_GEO_FENCES = gql`
  query EligibleGeoFences($reference: GeoReferenceInput!) {
    eligibleGeoFences(reference: $reference) {
      ...GeoFenceFields
    }
  }
  ${GEO_FENCE_FIELDS}
`
